/** Portal: send papers to the sheriff's office. The original paper (PDF) is required; the form is optional. */
import { useState } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { Card, Empty, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const EMPTY = { paperTypeCode: '', caseNumber: '', plaintiff: '', defendantName: '', line1: '', line2: '', city: '', state: 'SC', zip: '', courtDate: '', courtTime: '', specialInstructions: '' };
const when = (s: string) => new Date(s).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function SendPapers({ onBack }: { onBack: () => void }) {
  const meta = useLoad(() => api.get<any>('/api/portal/send-meta'));
  const sent = useLoad(() => api.get<any>('/api/portal/sent'));
  const [files, setFiles] = useState<File[]>([]);
  const [withForm, setWithForm] = useState(false);
  const [f, setF] = useState({ ...EMPTY });
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const a = useAction();
  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const send = () =>
    a.run(async () => {
      const payload = await Promise.all(files.map(async (x) => ({ filename: x.name, dataBase64: await fileToBase64(x) })));
      const r = await api.post<{ pages: number }>('/api/portal/send', { files: payload, form: withForm ? f : undefined, note: note.trim() || undefined });
      setDone(`Sent ${files.length} file${files.length === 1 ? '' : 's'} (${r.pages} page${r.pages === 1 ? '' : 's'}) to ${meta.data?.agency ?? 'the sheriff’s office'}.`);
      setFiles([]);
      setF({ ...EMPTY });
      setNote('');
      setWithForm(false);
      setInputKey((k) => k + 1);
      sent.reload();
    });
  return (
    <div className="stack">
      <div className="row between wrap gap">
        <h1>Send papers to {meta.data?.agency ?? 'the sheriff’s office'}</h1>
        <button className="btn" onClick={onBack}>
          ← My papers
        </button>
      </div>
      {done && <Notice tone="ok">✓ {done} You’ll see them in your list once the office enters them.</Notice>}
      <Card title="1. The original paper (required)">
        <p className="small muted">Scan or save the stamped paper as a PDF — one paper per upload works best. Several files are combined in the order you pick them.</p>
        <input
          key={inputKey}
          type="file"
          accept="application/pdf"
          multiple
          aria-label="Original paper PDF"
          onChange={(e) => (setFiles([...(e.target.files ?? [])]), setDone(null))}
        />
        {files.length > 0 && (
          <ul className="plain send-files">
            {files.map((x) => (
              <li key={x.name}>
                📄 {x.name} <span className="muted small">· {Math.max(1, Math.round(x.size / 1024))} KB</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="2. Details (optional)">
        <label className="row gap-sm">
          <input type="checkbox" checked={withForm} onChange={(e) => setWithForm(e.target.checked)} /> I’ll fill in the details so they’re entered exactly
        </label>
        {withForm && (
          <div className="stack send-form">
            <div className="grid2">
              <Field label="Paper type">
                <select value={f.paperTypeCode} onChange={set('paperTypeCode')}>
                  <option value="">—</option>
                  {(meta.data?.paperTypes ?? []).map((t: any) => (
                    <option key={t.code} value={t.code}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Case number">
                <input value={f.caseNumber} onChange={set('caseNumber')} />
              </Field>
              <Field label="Plaintiff / petitioner">
                <input value={f.plaintiff} onChange={set('plaintiff')} />
              </Field>
              <Field label="Person to serve / Defendant">
                <input value={f.defendantName} onChange={set('defendantName')} />
              </Field>
              <Field label="Street address">
                <input value={f.line1} onChange={set('line1')} />
              </Field>
              <Field label="Apt / unit">
                <input value={f.line2} onChange={set('line2')} />
              </Field>
              <Field label="City">
                <input value={f.city} onChange={set('city')} />
              </Field>
              <div className="row gap">
                <Field label="State">
                  <input value={f.state} onChange={set('state')} maxLength={2} style={{ width: 70 }} />
                </Field>
                <Field label="ZIP">
                  <input value={f.zip} onChange={set('zip')} maxLength={10} style={{ width: 110 }} />
                </Field>
              </div>
              <Field label="Court / hearing date">
                <input type="date" value={f.courtDate} onChange={set('courtDate')} />
              </Field>
              <Field label="Time">
                <input type="time" value={f.courtTime} onChange={set('courtTime')} />
              </Field>
            </div>
            <Field label="Special instructions">
              <textarea rows={2} value={f.specialInstructions} onChange={set('specialInstructions')} />
            </Field>
          </div>
        )}
        <Field label="Note to the office (optional)">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please serve before the 10th" />
        </Field>
      </Card>
      <ErrorBox error={a.error} />
      <div>
        <button className="btn primary big" disabled={!files.length || a.busy} onClick={send}>
          {a.busy ? 'Sending…' : files.length ? `Send ${files.length === 1 ? 'paper' : `${files.length} files`}` : 'Attach the original paper to send'}
        </button>
      </div>
      <Card title="Sent">
        {!sent.data ? (
          <Spinner />
        ) : sent.data.sent.length === 0 ? (
          <Empty>Nothing sent yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Sent</th>
                  <th>File</th>
                  <th>Status</th>
                  <th>Papers entered</th>
                </tr>
              </thead>
              <tbody>
                {sent.data.sent.map((x: any) => (
                  <tr key={x.id}>
                    <td className="small">
                      {when(x.sentAt)}
                      <div className="muted">{x.by}</div>
                    </td>
                    <td>
                      {x.filename}
                      <div className="small muted">
                        {x.pages} page{x.pages === 1 ? '' : 's'}
                        {x.note ? ` · “${x.note}”` : ''}
                      </div>
                    </td>
                    <td>{x.status}</td>
                    <td className="small">
                      {x.papers.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        x.papers.map((p: any) => (
                          <div key={p.id}>
                            <span className="mono">{p.number}</span> {p.defendant}
                          </div>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
